import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, injectIntl } from 'react-intl'
import PropTypes from 'prop-types'

import { displayFormula } from 'altrac-conversions'
import { SelectInput, TextInput, DecimalInput } from '../Inputs/Inputs'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import RedX from '../Graphics/RedX'

const DEFAULT_PHYSICAL = {
  r1FType: 'closure',
  r1FUse: 'machine1Feedback',
  r1FMin: 0,
  r1FLow: 0,
  r1FMax: 100,
  r1FLabel: 'Relay 1 Feedback',
  r2FType: 'closure',
  r2FUse: 'machine2Feedback',
  r2FMin: 0,
  r2FLow: 0,
  r2FMax: 100,
  r2FLabel: 'Relay 2 Feedback',
  anIn1Type: 'mA',
  aIn1Use: 'none',
  anIn1Min: 0,
  anIn1Max: 100,
  anIn1Label: 'Analog Sensor',
  anIn1Low: 0,
  anIn2Type: 'v',
  aIn2Use: 'none',
  anIn2Min: 0,
  anIn2Max: 100,
  anIn2Label: 'Accessory Port',
  anIn2Low: 0,
  ioDigUse1: 'none',
  ioDigUse2: 'none',
  ioDigUse3: 'none',
  calibrate1: 6.25,
  calibrate2: 6.25,
}

const ANALOG_INPUT_VALID = [
  { name: 'Contact, Power VIN', value: 'ON' },
  { name: '4-20mA, Power VIN', value: 'CV' },
  { name: '4-20mA, Power Off', value: 'CE' },
  { name: '0-10VDC, Power VIN', value: 'VV' },
  { name: '0-10VDC, Power 12.5V', value: 'VT' },
  { name: '0-10VDC, Power Off', value: 'VE' },
  // { name: '0-3VDC, Power VIN', value: 'FV' },
  // { name: '0-3VDC, Power 12.5V', value: 'FT' },
  // { name: '0-3VDC, Power Off', value: 'FE' },
]
const ANALOG_ACCESSORY_VALID = [
  { name: 'Contact, Power 12.5V', value: 'ON' },
  { name: '4-20mA, Power 12.5V', value: 'CV' },
  { name: '4-20mA, Power Off', value: 'CE' },
  { name: '0-10VDC, Power 12.5V', value: 'VV' },
  { name: '0-10VDC, Power Off', value: 'VE' },
  // { name: '0-3VDC, Power 12.5V', value: 'FV' },
  // { name: '0-3VDC, Power Off', value: 'FE' },
]

const FEEDBACK_INPUT_USE_VALID = [
  { name: 'No Feedback Use', value: 'none' },
  { name: 'Feedback for Relay 1', value: 'machine1Feedback' },
  { name: 'Feedback for Relay 2', value: 'machine2Feedback' },
]

const DIGITAL_INPUT_USE_VALID = [
  { name: 'Not In Use', value: 'none', io_dig_setting: 'DV' },
  { name: 'Auto Detect Pump 1', value: 'autoDetectPump1', io_dig_setting: 'DV' },
  { name: 'Auto Detect Pump 2', value: 'autoDetectPump2', io_dig_setting: 'DV' },
  { name: 'Feedback for Relay 1', value: 'machine1Feedback', io_dig_setting: 'DV' },
  { name: 'Feedback for Relay 2', value: 'machine2Feedback', io_dig_setting: 'DV' },
]

const DEFAULT_SETTINGS = {
  io_r1_feedback: 'ON',
  io_r2_feedback: 'ON',
  io_analog_sensor: 'CV',
  io_accessory: 'CV',
  io_dig_1: 'DV',
  io_dig_2: 'DV',
  io_dig_3: 'DV',
}

function pressure (formula, multiplier, valueKey, reading0, physical) {
  if (reading0[valueKey] > 0) {
    return displayFormula(
      formula,
      multiplier,
      1,
      null,
      valueKey,
      reading0,
      null,
      physical
    )
  }

  return 0
}

const typeConverter = (input) => {
  let returnValue = 'closure'
  switch (input.charAt(0)) {
    case 'O':
      returnValue = 'closure'
      break
    case 'C':
      returnValue = 'current4To20'
      break
    case 'V':
      returnValue = 'voltage0To10'
      break
    case 'F':
      returnValue = 'voltage0To3'
      break
    default:
      returnValue = 'closure'
      break
  }
  return returnValue
}

function Pump (props) {
  const navigate = useNavigate()

  const defaultSettings = { settings: { ...DEFAULT_SETTINGS } }

  const {
    device: {
      application_settings: applicationSettings = defaultSettings,
      application_settings_new: applicationSettingsNew = defaultSettings,
      physical = DEFAULT_PHYSICAL,
    } = {},
    device,
    setValidity,
    data: { deviceSettings: { invalid = false, newSettings, newDeviceInfo } = {} } = {},
  } = props

  const {
    settings: {
      io_r1_feedback: default_io_r1_feedback = DEFAULT_SETTINGS.io_r1_feedback,
      io_r2_feedback: default_io_r2_feedback = DEFAULT_SETTINGS.io_r2_feedback,
      io_analog_sensor: default_io_analog_sensor = DEFAULT_SETTINGS.io_analog_sensor,
      io_accessory: default_io_accessory = DEFAULT_SETTINGS.io_accessory,
      io_dig_1: default_io_dig_1 = DEFAULT_SETTINGS.io_dig_1,
      io_dig_2: default_io_dig_2 = DEFAULT_SETTINGS.io_dig_2,
      io_dig_3: default_io_dig_3 = DEFAULT_SETTINGS.io_dig_3,
      calibrate1: default_calibrate1 = DEFAULT_SETTINGS.calibrate1,
      calibrate2: default_calibrate2 = DEFAULT_SETTINGS.calibrate2,
    } = {},
  } = applicationSettings

  const {
    settings: {
      io_r1_feedback: new_io_r1_feedback,
      io_r2_feedback: new_io_r2_feedback,
      io_analog_sensor: new_io_analog_sensor,
      io_accessory: new_io_accessory,
      io_dig_1: new_io_dig_1,
      io_dig_2: new_io_dig_2,
      io_dig_3: new_io_dig_3,
      calibrate1: new_calibrate1,
      calibrate2: new_calibrate2,
    } = {},
  } = applicationSettingsNew

  const {
    r1FType: defaultr1FType = DEFAULT_PHYSICAL.r1FType,
    r1FUse: defaultr1FUse = DEFAULT_PHYSICAL.r1FUse,
    r1FMin: defaultr1FMin = DEFAULT_PHYSICAL.r1FMin,
    r1FLow: defaultr1FLow = DEFAULT_PHYSICAL.r1FLow,
    r1FMax: defaultr1FMax = DEFAULT_PHYSICAL.r1FMax,
    r1FLabel: defaultr1FLabel = DEFAULT_PHYSICAL.r1FLabel,
    r2FType: defaultr2FType = DEFAULT_PHYSICAL.r2FType,
    r2FUse: defaultr2FUse = DEFAULT_PHYSICAL.r2FUse,
    r2FMin: defaultr2FMin = DEFAULT_PHYSICAL.r2FMin,
    r2FLow: defaultr2FLow = DEFAULT_PHYSICAL.r2FLow,
    r2FMax: defaultr2FMax = DEFAULT_PHYSICAL.r2FMax,
    r2FLabel: defaultr2FLabel = DEFAULT_PHYSICAL.r2FLabel,
    anIn1Type: defaultanIn1Type = DEFAULT_PHYSICAL.anIn1Type,
    aIn1Use: defaultaIn1Use = DEFAULT_PHYSICAL.aIn1Use,
    anIn1Min: defaultanIn1Min = DEFAULT_PHYSICAL.anIn1Min,
    anIn1Max: defaultanIn1Max = DEFAULT_PHYSICAL.anIn1Max,
    anIn1Label: defaultanIn1Label = DEFAULT_PHYSICAL.anIn1Label,
    anIn1Low: defaultanIn1Low = DEFAULT_PHYSICAL.anIn1Low,
    anIn2Type: defaultanIn2Type = DEFAULT_PHYSICAL.anIn2Type,
    aIn2Use: defaultaIn2Use = DEFAULT_PHYSICAL.aIn2Use,
    anIn2Min: defaultanIn2Min = DEFAULT_PHYSICAL.anIn2Min,
    anIn2Max: defaultanIn2Max = DEFAULT_PHYSICAL.anIn2Max,
    anIn2Label: defaultanIn2Label = DEFAULT_PHYSICAL.anIn2Label,
    anIn2Low: defaultanIn2Low = DEFAULT_PHYSICAL.anIn2Low,
    ioDigUse1: defaultioDigUse1 = DEFAULT_PHYSICAL.ioDigUse1,
    ioDigUse2: defaultioDigUse2 = DEFAULT_PHYSICAL.ioDigUse2,
    ioDigUse3: defaultioDigUse3 = DEFAULT_PHYSICAL.ioDigUse3,
  } = physical

  const tempIoR1F = new_io_r1_feedback || default_io_r1_feedback
  const tempIoR2F = new_io_r2_feedback || default_io_r2_feedback

  const [formIsValid, setFormIsValid] = useState(true)
  const [invalidFields, setInvalidFields] = useState({})
  const [io_r1_feedback, setIoR1Feedback] = useState(tempIoR1F)
  const [io_r2_feedback, setIoR2Feedback] = useState(tempIoR2F)
  const [io_analog_sensor, setIoAnalogSensor] = useState(new_io_analog_sensor || default_io_analog_sensor)
  const [io_accessory, setIoAccessory] = useState(new_io_accessory || default_io_accessory)
  const [io_dig_1, setIoDig1] = useState(new_io_dig_1 || default_io_dig_1)
  const [io_dig_2, setIoDig2] = useState(new_io_dig_2 || default_io_dig_2)
  const [io_dig_3, setIoDig3] = useState(new_io_dig_3 || default_io_dig_3)
  const [r1FType, setR1FType] = useState(defaultr1FType)
  const [r1FUse, setR1FUse] = useState(defaultr1FUse)
  const [r1FMin, setR1FMin] = useState(tempIoR1F === 'ON' ? 0 : defaultr1FMin)
  const [r1FLow, setR1FLow] = useState(tempIoR1F === 'ON' ? 1 : defaultr1FLow)
  const [r1FMax, setR1FMax] = useState(tempIoR1F === 'ON' ? 16 : defaultr1FMax)
  const [r1FLabel, setR1FLabel] = useState(defaultr1FLabel)
  const [r2FType, setR2FType] = useState(defaultr2FType)
  const [r2FUse, setR2FUse] = useState(defaultr2FUse)
  const [r2FMin, setR2FMin] = useState(tempIoR2F === 'ON' ? 0 : defaultr2FMin)
  const [r2FLow, setR2FLow] = useState(tempIoR2F === 'ON' ? 1 : defaultr2FLow)
  const [r2FMax, setR2FMax] = useState(tempIoR2F === 'ON' ? 16 : defaultr2FMax)
  const [r2FLabel, setR2FLabel] = useState(defaultr2FLabel)
  const [anIn1Type, setAnIn1Type] = useState(defaultanIn1Type)
  const [aIn1Use, setAIn1Use] = useState(defaultaIn1Use)
  const [anIn1Min, setAnIn1Min] = useState(defaultanIn1Min)
  const [anIn1Max, setAnIn1Max] = useState(defaultanIn1Max)
  const [anIn1Label, setAnIn1Label] = useState(defaultanIn1Label)
  const [anIn1Low, setAnIn1Low] = useState(defaultanIn1Low)
  const [anIn2Type, setAnIn2Type] = useState(defaultanIn2Type)
  const [aIn2Use, setAIn2Use] = useState(defaultaIn2Use)
  const [anIn2Min, setAnIn2Min] = useState(defaultanIn2Min)
  const [anIn2Max, setAnIn2Max] = useState(defaultanIn2Max)
  const [anIn2Label, setAnIn2Label] = useState(defaultanIn2Label)
  const [anIn2Low, setAnIn2Low] = useState(defaultanIn2Low)
  const [ioDigUse1, setIoDigUse1] = useState(defaultioDigUse1)
  const [ioDigUse2, setIoDigUse2] = useState(defaultioDigUse2)
  const [ioDigUse3, setIoDigUse3] = useState(defaultioDigUse3)
  const [calibrate1, setCalibrate1] = useState(new_calibrate1 || default_calibrate1)
  const [calibrate2, setCalibrate2] = useState(new_calibrate2 || default_calibrate2)

  const appDispatch = useCallback(() => {
    const { address } = device

    const r1FTypeConverted = typeConverter(io_r1_feedback)
    const r2FTypeConverted = typeConverter(io_r2_feedback)
    const anIn1TypeConverted = typeConverter(io_analog_sensor)
    const anIn2TypeConverted = typeConverter(io_accessory)

    const settings = {
      address,
      status: 'new',
      settings: {
        io_r1_feedback,
        io_r2_feedback,
        io_analog_sensor,
        io_accessory,
        io_dig_1,
        io_dig_2,
        io_dig_3,
        calibrate1,
        calibrate2,
      },
    }

    const physicals = {
      r1FType: r1FTypeConverted,
      r1FUse,
      r1FMin,
      r1FLow,
      r1FMax,
      r1FLabel,
      r2FType: r2FTypeConverted,
      r2FUse,
      r2FMin,
      r2FLow,
      r2FMax,
      r2FLabel,
      anIn1Type: anIn1TypeConverted,
      aIn1Use,
      anIn1Min,
      anIn1Max,
      anIn1Label,
      anIn1Low,
      anIn2Type: anIn2TypeConverted,
      aIn2Use,
      anIn2Min,
      anIn2Max,
      anIn2Label,
      anIn2Low,
      ioDigUse1,
      ioDigUse2,
      ioDigUse3,
    }

    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: settings,
    })

    AppDispatcher.dispatch({
      action: 'change-physical',
      setting: physicals,
    })
  }, [device, io_r1_feedback, io_r2_feedback, io_analog_sensor, io_accessory, io_dig_1, io_dig_2, io_dig_3, r1FUse, r1FMin, r1FLow, r1FMax, r1FLabel, r2FUse, r2FMin, r2FLow, r2FMax, r2FLabel, aIn1Use, anIn1Min, anIn1Max, anIn1Label, anIn1Low, aIn2Use, anIn2Min, anIn2Max, anIn2Label, anIn2Low, ioDigUse1, ioDigUse2, ioDigUse3, calibrate1, calibrate2])

  useEffect(() => {
    appDispatch()
  }, [device, io_r1_feedback, io_r2_feedback, io_analog_sensor, io_accessory, io_dig_1, io_dig_2, io_dig_3, r1FUse, r1FMin, r1FLow, r1FMax, r1FLabel, r2FUse, r2FMin, r2FLow, r2FMax, r2FLabel, aIn1Use, anIn1Min, anIn1Max, anIn1Label, anIn1Low, aIn2Use, anIn2Min, anIn2Max, anIn2Label, anIn2Low, ioDigUse1, ioDigUse2, ioDigUse3, calibrate1, calibrate2])

  const childChange = useCallback((event) => {
    const { target: { name, value, validity: { valid } } } = event
    let newValue = value

    const lowCalc = (inp, min = 0, max = 100) => {
      if (inp.charAt(0) === 'C') {
        return (max - min) * (1 / 16) + min
      }
      if (inp.charAt(0) === 'V') {
        return (max - min) * (2 / 10) + min
      }
      return 1
    }

    switch (name) {
      case 'io_r1_feedback':
        if (value === 'ON') {
          setR1FMax(16)
          setR1FMin(0)
          setR1FLow(1)
        } else if (value.charAt(0) === 'C') {
          setR1FMax(100)
          setR1FMin(0)
          setR1FLow(6.25)
        } else if (value.charAt(0) === 'V') {
          setR1FMax(100)
          setR1FMin(0)
          setR1FLow(20)
        } else {
          setR1FMax(DEFAULT_PHYSICAL.r1FMax)
          setR1FMin(DEFAULT_PHYSICAL.r1FMin)
          setR1FLow(DEFAULT_PHYSICAL.r1FLow)
        }
        if (ANALOG_INPUT_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setIoR1Feedback(newValue)
        break
      case 'io_r2_feedback':
        if (value === 'ON') {
          setR2FMax(16)
          setR2FMin(0)
          setR2FLow(1)
        } else if (value.charAt(0) === 'C') {
          setR2FMax(100)
          setR2FMin(0)
          setR2FLow(6.25)
        } else if (value.charAt(0) === 'V') {
          setR2FMax(100)
          setR2FMin(0)
          setR2FLow(20)
        } else {
          setR2FMax(DEFAULT_PHYSICAL.r2FMax)
          setR2FMin(DEFAULT_PHYSICAL.r2FMin)
          setR2FLow(DEFAULT_PHYSICAL.r2FLow)
        }
        if (ANALOG_INPUT_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setIoR2Feedback(newValue)
        break
      case 'io_analog_sensor':
        if (value === 'ON') {
          setAnIn1Max(16)
          setAnIn1Min(0)
          setAnIn1Low(1)
        } else if (value.charAt(0) === 'C') {
          setAnIn1Max(100)
          setAnIn1Min(0)
          setAnIn1Low(6.25)
        } else if (value.charAt(0) === 'V') {
          setAnIn1Max(100)
          setAnIn1Min(0)
          setAnIn1Low(20)
        } else {
          setAnIn1Max(DEFAULT_PHYSICAL.anIn1Max)
          setAnIn1Min(DEFAULT_PHYSICAL.anIn1Min)
          setAnIn1Low(DEFAULT_PHYSICAL.anIn1Low)
        }
        if (ANALOG_INPUT_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setIoAnalogSensor(newValue)
        break
      case 'io_accessory':
        if (value === 'ON') {
          setAnIn2Max(16)
          setAnIn2Min(0)
          setAnIn2Low(1)
        } else if (value.charAt(0) === 'C') {
          setAnIn2Max(100)
          setAnIn2Min(0)
          setAnIn2Low(6.25)
        } else if (value.charAt(0) === 'V') {
          setAnIn2Max(100)
          setAnIn2Min(0)
          setAnIn2Low(20)
        } else {
          setAnIn2Max(DEFAULT_PHYSICAL.anIn2Max)
          setAnIn2Min(DEFAULT_PHYSICAL.anIn2Min)
          setAnIn2Low(DEFAULT_PHYSICAL.anIn2Low)
        }
        if (ANALOG_ACCESSORY_VALID.find((item) => item.value === value)) {
          // nothing to do here
        } else {
          newValue = DEFAULT_SETTINGS[name]
        }
        setIoAccessory(newValue)
        break
      case 'ioDigUse1': {
        const finder = DIGITAL_INPUT_USE_VALID.find((item) => item.value === value)
        if (finder) {
          setIoDigUse1(value)
          setIoDig1(finder.io_dig_setting || 'DV')
        } else {
          setIoDigUse1(DEFAULT_SETTINGS[name])
        }
      }
        break
      case 'ioDigUse2': {
        const finder = DIGITAL_INPUT_USE_VALID.find((item) => item.value === value)
        if (finder) {
          setIoDigUse2(value)
          setIoDig2(finder.io_dig_setting || 'DV')
        } else {
          setIoDigUse2(DEFAULT_SETTINGS[name])
        }
      }
        break
      case 'ioDigUse3': {
        const finder = DIGITAL_INPUT_USE_VALID.find((item) => item.value === value)
        if (finder) {
          setIoDigUse3(value)
          setIoDig3(finder.io_dig_setting || 'DV')
        } else {
          setIoDigUse3(DEFAULT_SETTINGS[name])
        }
      }
        break
      case 'r1FLabel':
        setR1FLabel(value)
        break
      case 'r2FLabel':
        setR2FLabel(value)
        break
      case 'anIn1Label':
        setAnIn1Label(value)
        break
      case 'anIn2Label':
        setAnIn2Label(value)
        break
      case 'r1FUse':
        setR1FUse(value)
        break
      case 'r2FUse':
        setR2FUse(value)
        break
      case 'aIn1Use':
        setAIn1Use(value)
        break
      case 'aIn2Use':
        setAIn2Use(value)
        break
      case 'r1FMin':
        setR1FMin(Number(value))
        setR1FLow(lowCalc(io_r1_feedback, Number(value), r1FMax))
        break
      case 'r1FMax':
        setR1FMax(Number(value))
        setR1FLow(lowCalc(io_r1_feedback, r1FMin, Number(value)))
        break
      case 'r2FMin':
        setR2FMin(Number(value))
        setR2FLow(lowCalc(io_r2_feedback, Number(value), r2FMax))
        break
      case 'r2FMax':
        setR2FMax(Number(value))
        setR2FLow(lowCalc(io_r2_feedback, r2FMin, Number(value)))
        break
      case 'anIn1Min':
        setAnIn1Min(Number(value))
        setAnIn1Low(lowCalc(io_analog_sensor, Number(value), anIn1Max))
        break
      case 'anIn1Max':
        setAnIn1Max(Number(value))
        setAnIn1Low(lowCalc(io_analog_sensor, anIn1Min, Number(value)))
        break
      case 'anIn2Min':
        setAnIn2Min(Number(value))
        setAnIn2Low(lowCalc(io_accessory, Number(value), anIn2Max))
        break
      case 'anIn2Max':
        setAnIn2Max(Number(value))
        setAnIn2Low(lowCalc(io_accessory, anIn2Min, Number(value)))
        break
      case 'r1FLow':
        setR1FLow(Number(value))
        break
      case 'r2FLow':
        setR2FLow(Number(value))
        break
      case 'anIn1Low':
        setAnIn1Low(Number(value))
        break
      case 'anIn2Low':
        setAnIn2Low(Number(value))
        break
      default:
        return
    }

    const newInvalidFields = { ...invalidFields }

    if (valid) {
      delete newInvalidFields[name]
    } else {
      newInvalidFields[name] = true
    }

    const newFormIsValid = Object.keys(newInvalidFields).length === 0

    setInvalidFields(newInvalidFields)
    setFormIsValid(newFormIsValid)
    setValidity(newFormIsValid)
  }, [invalidFields, setValidity, io_r1_feedback, io_r2_feedback, io_analog_sensor, io_accessory, r1FMin, r1FMax, r2FMin, r2FMax, anIn1Min, anIn1Max, anIn2Min, anIn2Max])

  const changeFeedbackThreshold = useCallback((input) => {
    const { id: deviceID, customer_id: customerID } = device

    if (deviceID && newDeviceInfo && Object.entries(newDeviceInfo).length > 0) {
      AppDispatcher.dispatch({
        action: 'set-device',
        deviceID,
        data: newDeviceInfo,
      })
    }

    if (
      newSettings &&
      (typeof newSettings.settings === 'object' || (Array.isArray(newSettings) && newSettings.length > 0))
    ) {
      AppDispatcher.dispatch({
        action: 'set-settings',
        settings: newSettings,
      })
    }

    navigate(`/${customerID}/devices/${deviceID}/settings/feedbackThreshold?input=${input}`)
  }, [device, newDeviceInfo, newSettings])

  const calOne = pressure('current4To20', 1, 'R1F', { R1F: calibrate1 }, physical)
  const calTwo = pressure('current4To20', 1, 'R2F', { R2F: calibrate2 }, physical)

  const redX = (
    <RedX
      style={{
        position: 'absolute',
        height: '20px',
        right: '4px',
        top: '8px',
        width: '20px',
      }}
    />
  )

  const textInputStyle = {
    height: '32px',
    position: 'relative',
    width: '100%',
  }

  const valueRequired = (value) => !value && redX
  const measurementLow = (sensorSet) => {
    if (sensorSet.charAt(0) === 'C') {
      return '4mA:'
    }
    return '0V:'
  }
  const measurementHigh = (sensorSet) => {
    if (sensorSet.charAt(0) === 'C') {
      return '20mA:'
    }
    if (sensorSet.charAt(0) === 'V') {
      return '10V:'
    }
    return '3V:'
  }

  return (
    <div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.pump.analogInput.label'
            defaultMessage='Analog Input Settings'
            description='Analog Input Settings label'
          />
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section name='r1F'>
          <h5>
            <FormattedMessage
              id='setting.pump.relay1Feedback.label'
              defaultMessage='Relay 1 Feedback'
              description='Relay 1 Feedback label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Setup:
            </h4>
            <SelectInput
              value={io_r1_feedback}
              options={ANALOG_INPUT_VALID}
              childChange={childChange}
              field='io_r1_feedback'
              style={styles.controlSelectInput}
            />
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Label:
            </h4>
            <div style={textInputStyle}>
              <TextInput
                childChange={childChange}
                field='r1FLabel'
                required
                style={styles.controlNumberInput}
                value={r1FLabel}
              />
              {valueRequired(r1FLabel)}
            </div>
          </div>
          {io_r1_feedback !== 'ON' && (
            <div name='scalingInputs'>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementLow(io_r1_feedback)}:
                </h4>
                <DecimalInput
                  value={r1FMin}
                  childChange={childChange}
                  field='r1FMin'
                  style={styles.controlNumberInput}
                />
              </div>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementHigh(io_r1_feedback)}:
                </h4>
                <DecimalInput
                  value={r1FMax}
                  childChange={childChange}
                  field='r1FMax'
                  style={styles.controlNumberInput}
                />
              </div>
            </div>
          )}
          {io_r1_feedback === 'CV' || io_r1_feedback === 'CE'
            ? (
              <div>
                <h5>
                  <FormattedMessage
                    id='setting.pump.analogSensor.feedbackThreshold'
                    defaultMessage='Feedback Threshold'
                    description='Feedback Threshold label'
                  />
                </h5>
                <button
                  className={styles.thresholdButton}
                  name='thresholdButton'
                  onClick={() => changeFeedbackThreshold(1)}
                  type='button'
                >
                  {calOne || '7 (Default)'}
                </button>
              </div>
              )
            : null}
          <br />
        </section>
        <section name='r2F'>
          <h5>
            <FormattedMessage
              id='setting.pump.relay2Feedback.label'
              defaultMessage='Relay 2 Feedback'
              description='Relay 2 Feedback label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Setup:
            </h4>
            <SelectInput
              value={io_r2_feedback}
              options={ANALOG_INPUT_VALID}
              childChange={childChange}
              field='io_r2_feedback'
              style={styles.controlSelectInput}
            />
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Label:
            </h4>
            <div style={textInputStyle}>
              <TextInput
                childChange={childChange}
                field='r2FLabel'
                required
                style={styles.controlNumberInput}
                value={r2FLabel}
              />
              {valueRequired(r2FLabel)}
            </div>
          </div>
          {io_r2_feedback !== 'ON' && (
            <div name='scalingInputs'>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementLow(io_r2_feedback)}:
                </h4>
                <DecimalInput
                  value={r2FMin}
                  childChange={childChange}
                  field='r2FMin'
                  style={styles.controlNumberInput}
                />
              </div>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementHigh(io_r2_feedback)}:
                </h4>
                <DecimalInput
                  value={r2FMax}
                  childChange={childChange}
                  field='r2FMax'
                  style={styles.controlNumberInput}
                />
              </div>
            </div>
          )}
          {io_r2_feedback === 'CV' || io_r2_feedback === 'CE'
            ? (
              <div>
                <h5>
                  <FormattedMessage
                    id='setting.pump.analogSensor.feedbackThreshold'
                    defaultMessage='Feedback Threshold'
                    description='Feedback Threshold label'
                  />
                </h5>
                <button
                  className={styles.thresholdButton}
                  name='thresholdButton'
                  onClick={() => changeFeedbackThreshold(2)}
                  type='button'
                >
                  {calTwo || '7 (Default)'}
                </button>
              </div>
              )
            : null}
          <br />
        </section>
        <section name='an1'>
          <h5>
            <FormattedMessage
              id='setting.pump.analogSensor.label'
              defaultMessage='Analog Sensor'
              description='Analog Sensor label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Setup:
            </h4>
            <SelectInput
              value={io_analog_sensor}
              options={ANALOG_INPUT_VALID}
              childChange={childChange}
              field='io_analog_sensor'
              style={styles.controlSelectInput}
            />
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Label:
            </h4>
            <div style={textInputStyle}>
              <TextInput
                childChange={childChange}
                field='anIn1Label'
                required
                style={styles.controlNumberInput}
                value={anIn1Label}
              />
              {valueRequired(anIn1Label)}
            </div>
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Use:
            </h4>
            <SelectInput
              value={aIn1Use}
              options={FEEDBACK_INPUT_USE_VALID}
              childChange={childChange}
              field='aIn1Use'
              style={styles.controlSelectInput}
            />
          </div>
          {io_analog_sensor !== 'ON' && (
            <div name='scalingInputs'>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementLow(io_analog_sensor)}:
                </h4>
                <DecimalInput
                  value={anIn1Min}
                  childChange={childChange}
                  field='anIn1Min'
                  style={styles.controlNumberInput}
                />
              </div>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementHigh(io_analog_sensor)}:
                </h4>
                <DecimalInput
                  value={anIn1Max}
                  childChange={childChange}
                  field='anIn1Max'
                  style={styles.controlNumberInput}
                />
              </div>
            </div>
          )}
          <br />
        </section>
        <section name='an2'>
          <h5>
            <FormattedMessage
              id='setting.pump.accessoryPort.label'
              defaultMessage='Accessory Port'
              description='Accessory Port label'
            />
          </h5>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Setup:
            </h4>
            <SelectInput
              value={io_accessory}
              options={ANALOG_ACCESSORY_VALID}
              childChange={childChange}
              field='io_accessory'
              style={styles.controlSelectInput}
            />
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Label:
            </h4>
            <div style={textInputStyle}>
              <TextInput
                childChange={childChange}
                field='anIn2Label'
                required
                style={styles.controlNumberInput}
                value={anIn2Label}
              />
              {valueRequired(anIn2Label)}
            </div>
          </div>
          <div className={styles.controlNumber}>
            <h4 className={styles.controlNumberLabel}>
              Use:
            </h4>
            <SelectInput
              value={aIn2Use}
              options={FEEDBACK_INPUT_USE_VALID}
              childChange={childChange}
              field='aIn2Use'
              style={styles.controlSelectInput}
            />
          </div>
          {io_accessory !== 'ON' && (
            <div name='scalingInputs'>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementLow(io_accessory)}:
                </h4>
                <DecimalInput
                  value={anIn2Min}
                  childChange={childChange}
                  field='anIn2Min'
                  style={styles.controlNumberInput}
                />
              </div>
              <div className={styles.controlNumber}>
                <h4 className={styles.controlNumberLabel}>
                  {measurementHigh(io_accessory)}:
                </h4>
                <DecimalInput
                  value={anIn2Max}
                  childChange={childChange}
                  field='anIn2Max'
                  style={styles.controlNumberInput}
                />
              </div>
            </div>
          )}
        </section>
      </div>
      <section className={styles.heading}>
        <h3>
          <FormattedMessage
            id='setting.pump.digitalInput.label'
            defaultMessage='Digital Input Settings'
            description='Digital Input Settings label'
          />
        </h3>
      </section>
      <div className={styles.inputArea}>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.digitalIn1.label'
              defaultMessage='Digital Input 1'
              description='Digital Input 1 label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={ioDigUse1}
              options={DIGITAL_INPUT_USE_VALID}
              childChange={childChange}
              field='ioDigUse1'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.digitalIn2.label'
              defaultMessage='Digital Input 2'
              description='Digital Input 2 label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={ioDigUse2}
              options={DIGITAL_INPUT_USE_VALID}
              childChange={childChange}
              field='ioDigUse2'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
        <section>
          <h5>
            <FormattedMessage
              id='setting.pump.digitalIn3.label'
              defaultMessage='Digital Input 3'
              description='Digital Input 3 label'
            />
          </h5>
          <div className={styles.controlSelect}>
            <SelectInput
              value={ioDigUse3}
              options={DIGITAL_INPUT_USE_VALID}
              childChange={childChange}
              field='ioDigUse3'
              style={styles.controlSelectInput}
            />
          </div>
        </section>
      </div>
    </div>
  )
}

Pump.propTypes = {
  device: PropTypes.object.isRequired,
  setValidity: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(Pump)
